/**
 * Proof surfaces for the auth flow.
 *
 * `AuthTestimonialRail` — three compact, stat-led customer cards sourced from
 * humblytics.com. Not rendered on sign-in since 2026-09-29: returning users need
 * no persuasion and the rail pushed the page past one viewport.
 * `MobileTrustStrip` is the one-line variant used on `/verify`.
 */
import { motion } from "framer-motion"

import {
  AUTH_TESTIMONIALS,
  SPOTLIGHT_TESTIMONIAL,
  type Testimonial,
} from "@/data/testimonials"

/** Compact one-line proof for tight surfaces (e.g. under the Verify card). */
export function MobileTrustStrip() {
  const t = SPOTLIGHT_TESTIMONIAL
  return (
    <p className="text-center text-xs leading-[18px] text-muted-foreground">
      <span className="font-medium tabular-nums text-foreground">{t.stat}</span>
      {" "}
      {t.statLabel} · Trusted by growth teams and agencies
    </p>
  )
}

function TestimonialCard({
  testimonial,
  index,
}: {
  testimonial: Testimonial
  index: number
}) {
  return (
    <motion.article
      className="surface-raised flex h-full flex-col gap-3 p-5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 + index * 0.06, ease: "easeOut" }}
    >
      {/* Stats are ink, never green: White signal v2 keeps color for the one CTA. */}
      <div className="flex items-baseline gap-2">
        <p className="text-[22px] font-semibold leading-7 tracking-[-0.015em] tabular-nums text-foreground">
          {testimonial.stat}
        </p>
        <p className="text-xs leading-4 text-muted-foreground">
          {testimonial.statLabel}
        </p>
      </div>

      <p className="flex-1 text-pretty text-[13px] leading-5 text-muted-foreground">
        &ldquo;{testimonial.quote}&rdquo;
      </p>

      {/* Attribution spans the full card width so roles never truncate. */}
      <div className="flex items-center gap-2.5 border-t border-border pt-3">
        <img
          src={testimonial.avatar}
          alt=""
          aria-hidden
          className="size-7 shrink-0 rounded-full object-cover ring-1 ring-border"
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-[13px] font-medium leading-4 text-foreground">
            {testimonial.name}
          </p>
          <p className="truncate text-xs leading-4 text-muted-foreground">
            {testimonial.role}
          </p>
        </div>
      </div>
    </motion.article>
  )
}

/**
 * Proof for /auth sign-in: three stat-led customer cards on the gray canvas,
 * under the sign-in card. Sentence-case heading, ink stats.
 */
export function AuthTestimonialRail() {
  return (
    <section
      aria-label="Customer results"
      className="relative w-full shrink-0 px-4 pb-10 sm:px-8"
    >
      <div className="mx-auto flex w-full max-w-[896px] flex-col items-center gap-4">
        <p className="text-center text-[13px] font-medium leading-5 text-muted-foreground">
          Trusted by growth teams and agencies
        </p>
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
          {AUTH_TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={t.id} testimonial={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
